import React, { useEffect } from "react";
import * as Yup from "yup";
import { Form, Formik } from "formik";
import {
  selectRedirectToDashboard,
  selectResetPasswordResponse,
  selectUpdatingPassword,
  setRedirectToDashboard,
  updatePassword,
} from "./PasswordChangeSlice";
import { useDispatch, useSelector } from "react-redux";
import { useThunkDispatch } from "setup/redux/thunkDispatch";
import { passwordValidationRegex } from "cpl-react-common/components/Constants";
import CPLPasswordInput from "cpl-react-common/components/Inputs/CPLPasswordInput";

type Props = {
  userID?: string;
  redirectToDashboard: boolean;
};

const passwordChangeSchema = Yup.object().shape({
  password: Yup.string()
    .required("Please enter your new password")
    .matches(
      passwordValidationRegex,
      "Password must be at least 8 characters in length and contain one number, one upper case letter and one lower case letter"
    ),
  repeatPassword: Yup.string()
    .required("Please re-enter your password")
    .oneOf([Yup.ref("password")], "Password does not match"),
});

const PasswordChange: React.FC<Props> = ({ userID, redirectToDashboard }) => {
  const thunkDispatch = useThunkDispatch();
  const dispatch = useDispatch();
  const updatingPassword = useSelector(selectUpdatingPassword);

  const redirect = useSelector(selectRedirectToDashboard);
  const response = useSelector(selectResetPasswordResponse);

  useEffect(() => {
    dispatch(setRedirectToDashboard(redirectToDashboard));
    if (redirect && response) {
      window.location.href = "/dashboard";
    }
  }, [redirect, response, dispatch, redirectToDashboard]);

  const initialValues = {
    password: "",
    repeatPassword: "",
  };

  return (
    <div className="card mb-5 mb-xl-10">
      <div className="card-header">
        <div className="card-title m-0">
          <h3 className="fw-bolder m-0">Change Password</h3>
        </div>
      </div>
      <div className="card-body border-top p-9">
        <Formik
          enableReinitialize
          initialValues={initialValues}
          onSubmit={async (values) => {
            thunkDispatch(updatePassword({ newPassword: values.password, userID }));
          }}
          validationSchema={passwordChangeSchema}
        >
          {({ getFieldProps }) => (
            <Form>
              <div className="row mb-6">
                <label className="col-lg-4 col-form-label required fw-bold fs-6">Password</label>
                <div className="col-lg-8">
                  <div className="row">
                    <div className="col-lg-12 fv-row">
                      <CPLPasswordInput
                        data-test-id="password-change-form__password-input"
                        id="password"
                        autocomplete="off"
                        getFieldProps={getFieldProps}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="row mb-6">
                <label className="col-lg-4 col-form-label required fw-bold fs-6">Re-enter Password</label>
                <div className="col-lg-8">
                  <div className="row">
                    <div className="col-lg-12 fv-row">
                      <CPLPasswordInput
                        data-test-id="password-change-form__repeat-password-input"
                        id="repeatPassword"
                        autocomplete="off"
                        getFieldProps={getFieldProps}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <button
                data-test-id="password-change-form__submit-button"
                type="submit"
                className="btn btn-primary"
                disabled={updatingPassword}
              >
                {!updatingPassword && "Save Changes"}
                {updatingPassword && (
                  <span className="indicator-progress" style={{ display: "block" }}>
                    Please wait... <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                  </span>
                )}
              </button>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default PasswordChange;
